'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, isValidElement, useState } from 'react';
import { toast } from 'react-hot-toast';

interface QueryProviderProps {
  children: ReactNode;
}

const patchSafeToastOnce = () => {
  const anyToast = toast as any;
  if (anyToast.__alpacoSafeToastPatched) return;

  const coerceRenderable = (value: any): any => {
    if (typeof value === 'function') {
      return (t: any) => coerceRenderable(value(t));
    }
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }
    if (isValidElement(value)) return value;
    if (Array.isArray(value)) {
      const parts = value
        .map((v) => coerceRenderable(v))
        .map((v) => (typeof v === 'string' ? v : v === null ? '' : JSON.stringify(v)))
        .filter(Boolean);
      return parts.join('\n') || 'Có lỗi xảy ra';
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const wrap = (fn: any) => (message: any, options?: any) => fn(coerceRenderable(message), options);

  if (typeof anyToast.error === 'function') anyToast.error = wrap(anyToast.error);
  if (typeof anyToast.success === 'function') anyToast.success = wrap(anyToast.success);
  if (typeof anyToast.loading === 'function') anyToast.loading = wrap(anyToast.loading);
  if (typeof anyToast.custom === 'function') anyToast.custom = wrap(anyToast.custom);

  anyToast.__alpacoSafeToastPatched = true;
};

patchSafeToastOnce();

export default function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Thời gian cache mặc định: 5 phút
            staleTime: 1000 * 60 * 5,
            // Thời gian giữ data trong cache: 10 phút
            gcTime: 1000 * 60 * 10,
            // Retry khi lỗi
            retry: 1,
            refetchOnMount: false,
            // Refetch khi window focus
            refetchOnWindowFocus: false,
            // Refetch khi reconnect
            refetchOnReconnect: false,
            // Disable auto refetch globally
            refetchInterval: false,
            refetchIntervalInBackground: false,
          },
          mutations: {
            // Không retry mutation vì không idempotent
            retry: 0,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* React Query Devtools - chỉ hiển thị trong development */}
      {/* {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )} */}
    </QueryClientProvider>
  );
}
